import '@fontsource/instrument-serif';

import Flash from '@/components/flash';
import CaprockMark from '@/components/linear/caprock-mark';
import { Toaster } from '@/components/ui/toaster';
import { root_path } from '@/rails/routes';
import { Head, Link, usePage } from '@inertiajs/react';
import { useEffect, type ReactNode } from 'react';

const FOCUS_RING =
  'rounded-sm focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring';

interface AuthLayoutProps {
  children: ReactNode;
  flash: {
    notice?: string;
    error?: string;
  };
  pageTitle?: string;
}

// Chrome for log in, password reset, and signup: one card on the paper background.
// Each page supplies its own heading and form.
const AuthLayout = ({ children, flash, pageTitle }: AuthLayoutProps) => {
  const { props } = usePage<{ pageTitle?: string; error?: string | null }>();
  const title = pageTitle || props.pageTitle || 'Log in · CAP Rock Labs';
  // A refused log in arrives twice: as the page's own error, shown beside the
  // form, and as a flash message. Toasting it as well would say it twice.
  const shownByPage = Boolean(props.error);

  // A light surface. A dark preference left by the workspace must not leak in.
  useEffect(() => {
    document.documentElement.classList.remove('dark');
    document.documentElement.removeAttribute('data-linear-theme');
  }, []);

  return (
    <>
      <Head title={title} />
      <div
        data-authority
        className="bg-background text-foreground flex min-h-screen flex-col items-center justify-center px-4 py-12"
      >
        <Link href={root_path()} className={`flex items-center gap-2.5 ${FOCUS_RING}`}>
          <CaprockMark size={32} />
          <span className="authority-display text-xl">CAP Rock Labs</span>
        </Link>

        <main className="bg-card text-card-foreground mt-8 w-full max-w-sm border p-6 sm:p-8">
          {children}
        </main>

        <Link
          href={root_path()}
          className={`text-muted-foreground hover:text-foreground mt-6 text-sm ${FOCUS_RING}`}
        >
          Back to home
        </Link>

        {shownByPage ? null : <Flash {...flash} />}
        <Toaster />
      </div>
    </>
  );
};

export default AuthLayout;
